import { Injectable } from '@angular/core';
import {
  GenericViewComponent,
  GenericViewDisplayConfig,
  GenericViewEndUserDto,
  GenericViewGroup,
} from '@osos/shared/api';
import {
  GenericViewPresenterModel,
  PageContainerLayoutMode,
  PageDisplayOptions,
} from '@osos/shared/types';
import { ParserBase } from '../../bases';

type PageContainerLayoutType = { mode: PageContainerLayoutMode };

@Injectable()
export class PublicGenericViewsParserV1Service extends ParserBase<
  GenericViewEndUserDto,
  GenericViewPresenterModel
> {
  constructor() {
    super(GenericViewEndUserDto, GenericViewPresenterModel, 1);
  }

  getPresenterModel(model: GenericViewEndUserDto): GenericViewPresenterModel {
    return {
      id: model.id,
      name: model.name,
      description: model.description,
      specificationVersion: 1,
      sharing: undefined,
      pageType: model.pageType,
      components: model.components,
      groups: model.groups,
      embeddedPageConfig: model.embeddedPageConfig,
      sidebarConfig: model?.sideBarConfig ?? {
        openMode: 0,
      },
      icon: model.icon,

      displayConfig: model?.displayConfig
        ? this._mapDisplayConfigToPresenterModel(model.displayConfig)
        : this._getDefaultDisplayOptions(),

      uiAttributes: model.uiAttributes,
      allowExportPDF: model.allowExportPDF,
      autoRefresh: model.autoRefresh?.enabled,
      autoRefreshInterval: model.autoRefresh?.interval,

      redirectionRules: model.redirection
        ? JSON.stringify(model.redirection)
        : undefined,
      help: model.help,
    };
  }
  getDataModel(presenter: GenericViewPresenterModel): GenericViewEndUserDto {
    return {
      id: presenter.id,
      name: presenter.name,
      description: presenter.description,
      sideBarConfig: presenter.sidebarConfig,
      displayConfig: this._mapDisplayConfigToDataModel(presenter.displayConfig),
      components: presenter.components
        ? presenter.components.map(
            (cmp) =>
              ({
                ...cmp,
                icon: cmp.icon?.name ? cmp.icon : null,
              }) as GenericViewComponent,
          )
        : undefined,
      groups: presenter.groups
        ? (presenter.groups as GenericViewGroup[])
        : undefined,
      uiAttributes: presenter.uiAttributes,
      allowExportPDF: presenter.allowExportPDF,
      autoRefresh:
        presenter.autoRefresh !== undefined && presenter.autoRefresh !== null
          ? {
              enabled: presenter.autoRefresh,
              interval: presenter.autoRefreshInterval,
            }
          : undefined,
      pageType: presenter.pageType,
      embeddedPageConfig: presenter.embeddedPageConfig,
      icon: presenter.icon,

      redirection: presenter?.redirectionRules
        ? JSON.parse(presenter.redirectionRules)
        : undefined,
      help: presenter.help,
    };
  }

  private _mapDisplayConfigToPresenterModel(
    config: GenericViewDisplayConfig,
  ): PageDisplayOptions {
    const { pageContainerLayout, ...model } = config;

    return {
      ...model,
      pageContainedWidth:
        pageContainerLayout?.mode === PageContainerLayoutMode.Contained,
    };
  }

  private _mapDisplayConfigToDataModel(
    config: PageDisplayOptions,
  ): GenericViewDisplayConfig {
    const { pageContainedWidth, ...model } = config;

    return {
      ...model,
      pageContainerLayout: {
        mode: pageContainedWidth
          ? PageContainerLayoutMode.Contained
          : PageContainerLayoutMode.Fluid,
      } satisfies PageContainerLayoutType,
    };
  }

  private _getDefaultDisplayOptions(): PageDisplayOptions {
    return {
      transparent: false,
      fullPage: false,
      outerSpacing: true,
      showPageHeader: true,
      pageContainedWidth: false,
    };
  }
}
